/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Action, NgxsOnInit, Selector, SelectorOptions, State, StateContext } from '@ngxs/store';
import {
    AddLayerSelection,
    ClearSelection,
    MergeLayerSelection,
    MergeSelection,
    RefreshSelection,
    SetSelection,
} from './selection.actions';
import { SelectionFeatures, SelectionModel } from './selection.model';
import { FeatureFormatter } from './featureFormatter';
import { DashGisUtil } from '../../shared/dash-gis-util';
import { Injectable } from '@angular/core';
import { patch } from '@ngxs/store/operators';

@State<SelectionModel>({
    name: 'selection',
    defaults: {
        layers: {},
    },
})
@SelectorOptions({injectContainerState: false})
@Injectable()
export class SelectionState implements NgxsOnInit {

    constructor() {
    }

    ngxsOnInit(ctx: StateContext<SelectionModel>): void {
    }

    @Action(SetSelection)
    setSelection({ setState }: StateContext<SelectionModel>, action: SetSelection) {
        if (action.payload) {
            action.payload.forEach((layerFeatures) => {
                setState(
                    patch({
                        layers: patch({
                            [layerFeatures.layer]: layerFeatures.featureIds,
                        }),
                    })
                );
            });
        }
    }

    @Action(AddLayerSelection)
    addToSelection({ getState, setState }: StateContext<SelectionModel>, action: AddLayerSelection) {
        setState(
            patch({
                layers: patch({
                    [action.payload.layer]: action.payload.featureIds,
                }),
            })
        );
    }

    @Action(MergeSelection)
    mergeSelectionState({ getState, setState }: StateContext<SelectionModel>, action: MergeSelection) {
        let state: SelectionModel = DashGisUtil.clone(getState());
        if (action.payload.clearSelection) {
            state.layers = {};
        }
        for (const layerSelection of action.payload.selectionFeatures) {
            state = SelectionState.mergeLayerSelection(state, layerSelection.layer, layerSelection.featureIds);
        }
        setState(state);
    }

    @Action(MergeLayerSelection)
    mergeLayerSelectionState({ getState, setState }: StateContext<SelectionModel>, action: MergeLayerSelection) {
        const currentState: SelectionModel = DashGisUtil.clone(getState());
        const layer = action.payload.layer;

        const newState = SelectionState.mergeLayerSelection(currentState, layer, action.payload.featureIds);
        setState(newState);
    }

    @Action(ClearSelection)
    clearSelection({ setState }: StateContext<SelectionModel>) {
        setState({ layers: {} });
    }

    @Action(RefreshSelection)
    refreshSelection() {}

    @Selector([SelectionState])
    static layerSelection(state: SelectionModel) {
        return (layer) => SelectionState.getLayerSelection(state, layer);
    }

    @Selector([SelectionState])
    static selection(state: SelectionModel): SelectionFeatures {
        return state.layers;
    }

    @Selector([SelectionState])
    static hasSelection(state: SelectionModel): boolean {
        let found = false;
        const layers = state.layers;
        for (const layer in layers) {
            if (layers.hasOwnProperty(layer)) {
                const layerSelection = layers[layer];
                if (layerSelection) {
                    found = true;
                }
            }
        }
        return found;
    }

    private static mergeLayerSelection(state: SelectionModel, layer: string, featureIds: string[]): SelectionModel {
        const existingFeatureIds: string[] = state.layers[layer] || [];

        featureIds.forEach((featureId) => {
            const idx = existingFeatureIds.findIndex((existingId) => featureId === existingId);
            if (idx === -1) {
                existingFeatureIds.push(featureId);
            } else {
                existingFeatureIds.splice(idx, 1);
            }
        });

        if (existingFeatureIds.length > 0) {
            state.layers[layer] = existingFeatureIds;
        } else {
            delete state.layers[layer];
        }

        return state;
    }

    private static getLayerSelection(state, layer) {
        if (state.layers.hasOwnProperty(layer)) {
            const featureString: string = state.layers[layer];
            const layerSelection = FeatureFormatter.deserialize(featureString);
            if (layerSelection.length > 0) {
                return FeatureFormatter.deserialize(featureString);
            }
        }
    }
}
